import React, { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import LoadingComponent from '../../../../components/LoadingComponent'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import AttachmentManager, { ExistingAttachment } from '../../../../components/AttachmentManager'
import {
  Card,
  CollapsibleHeader,
  Separator,
  Title,
  Button,
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  FormGroup,
  Label,
  InputText,
  Select,
  FormGrid,
  BreadCrumble,
  FirstText,
  SecondText,
  InfoLink,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ProductShort,
  ProductDiv,
  ShortLabel,
  ActionsDiv,
  ProductFieldLabel,
  ProductFieldsContainer,
} from './style'
import AddProductForm from '../AddProductForm'
import CompanySelect from '../SelectItem/CompanySelect'
import VendorSelect from '../SelectItem/VendorSelect'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'

interface FashionEditContent {
  handleEditSave: React.MutableRefObject<() => Promise<void>>
}

type SizeQuantities = { [size: string]: number }

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

interface ProductApiData {
  fashionProductId?: number
  name?: string
  totalItems?: number
  subtotal?: number
  discount?: number
  colorCount?: number
  items?: FashionOrderProductItemInput[]
  fashionProduct?: {
    id: number
    name: string
    price: number
    group?: {
      id: number
      name: string
      family?: {
        id: number
        name: string
      }
    }
  }
  family?: { name: string }
  group?: { name: string }
  sizeQuantities?: SizeQuantities
  colors?: string[]
  selectedColors?: number[]
  discountValue?: number
}

interface CompanyItemType {
  id: number
  companyName: string
}

interface UserItemType {
  id: number
  name: string
}

interface FreightType {
  value: string
  label: string
}

interface PaymentCondition {
  name: string
}

interface EditFashionOrderPayload {
  orderType: string;
  trackingCode: string;
  extraInformation: string;
  deliveryTime: string;
  maximumBillingAmount: string;
  quantityItems: string;
  quantityPieces: string;
  totalCost: number;
  shippingCost: number;
  finalDateOfDelivery: string | null;
  startDateOfDelivery: string | null;
  supplier: CompanyItemType;
  seller: UserItemType;
  company: CompanyItemType;
  shippingType: string;
  paymentCondition: string;
  products: any[];
  attachments?: {
    file: string;
    fileName: string;
  }[];
}

const calculateDiscountValue = (subtotal: number, discountPercentage: number): number => {
  return (subtotal * discountPercentage) / 100;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      resolve(result)
    }
    reader.onerror = (error) => reject(error)
  })
}



const EditFashionOrder = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const { orderId } = useParams()
  const { handleEditSave, setIsSaving: setParentSaving } = useOutletContext<FashionEditContent & { setIsSaving?: (val: boolean) => void }>()
  
  useEffect(() => {
    console.log('🔍 [EDIT-ORDER] Contexto recebido:', { 
      hasHandleEditSave: !!handleEditSave, 
      hasSetIsSaving: !!setParentSaving 
    })
  }, [handleEditSave, setParentSaving])
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  const { setFeedback } = React.useContext(FeedbackContext)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fileUploadStatus, setFileUploadStatus] = useState<string>("")

  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([])

  const [formData, setFormData] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: '',
    shippingType: '',
    trackingCode: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '',
    quantityPieces: '',
    totalCost: '',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })

  const [freightTypes, setFreightTypes] = useState<FreightType[]>([])
  const [paymentOptions, setPaymentOptions] = useState<string[]>([])
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [newshippingType, setNewshippingType] = useState('')
  const [isAddingShipping, setIsAddingShipping] = useState(false)

  const cleanCurrency = useCallback((value: any): number => {
    const stringValue = String(value || "")
    const number = Number(stringValue.replace(/[^0-9,-]+/g, "").replace(",", "."))
    return isNaN(number) ? 0 : number
  }, [])

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatForInput = useCallback((value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "R$ 0,00"
    }

    const numericValue = typeof value === "string" ? Number(value.replace(/\D/g, "")) / 100 : Number(value)

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue)
  }, [])

  const formatDate = useCallback((isoDateString: string | null | undefined): string => {
    if (!isoDateString) return ""
    return isoDateString.substring(0, 10)
  }, [])

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, [])

  const handleRemoveExistingAttachment = useCallback(async (attachmentId: number) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      try {
        const token = localStorage.getItem('token')
        await axios.delete(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachmentId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId))
        alert('Anexo removido com sucesso!')
      } catch (error) {
        console.error('Erro ao remover anexo:', error)
        alert('Erro ao remover anexo. Tente novamente.')
      }
    }
  }, [orderId])

  const handleViewAttachment = useCallback(async (attachment: ExistingAttachment) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachment.id}/view`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      const { url } = response.data
      window.open(url, '_blank')
    } catch (error) {
      console.error('Erro ao visualizar anexo:', error)
      alert('Erro ao visualizar o arquivo. Tente novamente.')
    }
  }, [orderId])

  const handleDownloadAttachment = useCallback(async (attachment: ExistingAttachment) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachment.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      const { url } = response.data
      
      const link = document.createElement('a')
      link.href = url
      link.download = attachment.originalName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Erro ao baixar anexo:', error)
      alert('Erro ao baixar o arquivo. Tente novamente.')
    }
  }, [orderId])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024
    const validFiles: File[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`Arquivo ${file.name} excede 200MB e será ignorado.`)
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles(prev => [...prev, ...validFiles])
    setFileUploadStatus("")
    event.target.value = ""
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const normalizeProductData = useCallback((product: any): ProductApiData => {
    const sizeQuantities: SizeQuantities = {}

    if (product.items && Array.isArray(product.items)) {
      product.items.forEach((item: FashionOrderProductItemInput) => {
        if (item.size && item.quantity !== undefined) {
          sizeQuantities[item.size] =
            (sizeQuantities[item.size] || 0) + Number(item.quantity)
        }
      })
    }

    const totalItems = Object.values(sizeQuantities).reduce(
      (sum, qty) => sum + qty,
      0
    )

    const price = product.fashionProduct?.price || 0

    const subtotal = product.subtotal ?? totalItems * price

    const colorCount =
      product.colorCount ??
      (product.items
        ? new Set(product.items.map((item: any) => item.colorId)).size
        : 0)

    return {
      ...product,
      sizeQuantities,
      totalItems,
      colorCount,
      items: product.items || [],
      subtotal,
      fashionProduct: product.fashionProduct || {},
    }
  }, [])


  const fetchInitialData = async () => {
    try {
      const [paymentRes, shippingRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`),
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`),
      ])

      const paymentConditions = paymentRes.data.map((item: PaymentCondition) => item.name)
      const shippingTypes = shippingRes.data.map((item: { name: string }) => ({
        value: item.name,
        label: item.name,
      }))
      setPaymentOptions(paymentConditions)
      setFreightTypes(shippingTypes)
      return { paymentConditions, shippingTypes }
    } catch (err) {
      console.error('Erro ao buscar dados iniciais:', err)
      setPaymentOptions([])
      setFreightTypes([])
      return { paymentConditions: [], shippingTypes: [] }
    }
  }

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}`)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar o pedido:', error)
      return null
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      await fetchInitialData()
      const orderData = await fetchOrder()

      if (orderData) {
        setFormData({
          supplier: {
            id: Number(orderData.supplierId),
            companyName: orderData.supplier?.companyName || '',
          },
          seller: {
            id: orderData.seller?.id || 0,
            name: orderData.seller?.name || '',
          },
          company: {
            id: Number(orderData.companyId),
            companyName: orderData.company?.companyName || '',
          },
          orderType: orderData.orderType || '',
          paymentCondition: orderData.paymentCondition?.name || '',
          shippingType: orderData.shippingType?.name || '',
          trackingCode: orderData.trackingCode || '',
          finalDateOfDelivery: formatDate(orderData.finalDateOfDelivery),
          startDateOfDelivery: formatDate(orderData.startDateOfDelivery),
          extraInformation: orderData.extraInformation || '',
          quantityItems: orderData.quantityItems || '0',
          quantityPieces: orderData.quantityPieces || '0',
          totalCost: `R$ ${Number(orderData.totalCost || 0)
            .toFixed(2)
            .replace('.', ',')}`,
          deliveryTime: orderData.deliveryTime || '',
          shippingCost: orderData.shippingCost || '',
          maximumBillingAmount: orderData.maximumBillingAmount || '',
        })

        if (orderData.fileInFashionOrder && Array.isArray(orderData.fileInFashionOrder)) {
          const mappedAttachments = orderData.fileInFashionOrder.map((file: any) => ({
            id: file.generalFile.id,
            originalName: file.generalFile.originalName || file.generalFile.title,
            fileName: file.generalFile.fileName,
            filePath: file.generalFile.fileUrl,
            fileSize: file.generalFile.fileSize,
            mimeType: file.generalFile.mimeType,
          }));
          setExistingAttachments(mappedAttachments)
        } else {
          setExistingAttachments([])
        }

        if (orderData.products && Array.isArray(orderData.products)) {
          const normalizedProducts = orderData.products.map((product: any) =>
            normalizeProductData(product)
          )
          setAddedProducts(normalizedProducts)
        } else {
          setAddedProducts([])
        }
      }
      setLoading(false)
    }

    if (orderId) {
      loadData()
    }
  }, [orderId, formatDate, normalizeProductData])

  const handleAddPaymentCondition = async () => {
    if (newPaymentCondition && !paymentOptions.includes(newPaymentCondition)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
          {
            name: newPaymentCondition,
          },
        )
        if (response.status === 201) {
          setPaymentOptions([...paymentOptions, newPaymentCondition])
          setFormData({ ...formData, paymentCondition: newPaymentCondition })
          setNewPaymentCondition('')
          setIsAddingPayment(false)
        }
      } catch (error) {
        console.error('Erro ao salvar a nova condição de pagamento:', error)
      }
    }
  }

  const handleAddshippingType = async () => {
    if (newshippingType && !freightTypes.some((ft) => ft.value === newshippingType)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
          {
            name: newshippingType,
          },
        )
        if (response.status === 201) {
          setFreightTypes([...freightTypes, { value: newshippingType, label: newshippingType }])
          setFormData({ ...formData, shippingType: newshippingType })
          setNewshippingType('')
          setIsAddingShipping(false)
        }
      } catch (error) {
        console.error('Erro ao salvar o novo tipo de frete:', error)
      }
    }
  }

  const handleEditSaveOrder = useCallback(async () => {
    try {
      console.log('🔍 [EDIT-ORDER] setParentSaving disponível?', !!setParentSaving)
      if (setParentSaving) {
        console.log('✅ [EDIT-ORDER] Ativando loading...')
        setParentSaving(true)
      } else {
        console.warn('⚠️ [EDIT-ORDER] setParentSaving não está disponível!')
      }
      const groupedProductsMap = new Map();

      addedProducts.forEach(product => {
        const productId = product.fashionProduct?.id || product.fashionProductId;

        if (productId === undefined) {
          console.error("Produto sem ID, ignorando...");
          return;
        }

        if (groupedProductsMap.has(productId)) {
          const existingProduct = groupedProductsMap.get(productId)
          existingProduct.items = [...(existingProduct.items || []), ...(product.items || [])]
          existingProduct.totalItems += product.totalItems || 0
          existingProduct.subtotal += product.subtotal || 0
        } else {
          groupedProductsMap.set(productId, { ...product })
        }
      })

      const productsToSend = Array.from(groupedProductsMap.values())

      const payload: EditFashionOrderPayload = {
        orderType: formData.orderType,
        trackingCode: formData.trackingCode,
        extraInformation: formData.extraInformation,
        deliveryTime: formData.deliveryTime,
        maximumBillingAmount: String(cleanCurrency(formData.maximumBillingAmount)),
        quantityItems: formData.quantityItems,
        quantityPieces: formData.quantityPieces,

        totalCost: cleanCurrency(formData.totalCost),
        shippingCost: cleanCurrency(formData.shippingCost),

        finalDateOfDelivery: formData.finalDateOfDelivery ? new Date(formData.finalDateOfDelivery).toISOString() : null,
        startDateOfDelivery: formData.startDateOfDelivery ? new Date(formData.startDateOfDelivery).toISOString() : null,

        supplier: formData.supplier,
        seller: formData.seller,
        company: formData.company,
        shippingType: formData.shippingType,
        paymentCondition: formData.paymentCondition,

        products: productsToSend.map((product) => {
          let itemsToSend = product.items || []

          if ((!itemsToSend || itemsToSend.length === 0) && product.colorCount && product.colorCount > 0) {
            if (product.selectedColors && Array.isArray(product.selectedColors) && product.selectedColors.length > 0) {
              itemsToSend = product.selectedColors.map((colorId: any) => ({
                colorId: colorId,
                size: "N/A",
                quantity: 0,
              }))
            } else if (product.colors && Array.isArray(product.colors) && product.colors.length > 0) {
              itemsToSend = product.colors.map((colorInfo: { id: any }, index: number) => ({
                colorId: typeof colorInfo === "object" ? colorInfo.id : index + 1,
                size: "N/A",
                quantity: 0,
              }))
            } else {
              itemsToSend = Array.from({ length: product.colorCount }, (_, index) => ({
                colorId: index + 1,
                size: "N/A",
                quantity: 0,
              }))
            }
          }

          if (product.sizeQuantities && Object.keys(product.sizeQuantities).length > 0) {
            const sizeItems = Object.entries(product.sizeQuantities).map(([size, quantity]) => ({
              size,
              quantity: Number(quantity) || 0,
              colorId: 1,
            }))

            if (itemsToSend.length > 0) {
              itemsToSend = itemsToSend.map((item: { size: string; quantity: any; colorId: any }) => {
                const sizeMatch = sizeItems.find(si => si.size === item.size)
                return {
                  ...item,
                  quantity: sizeMatch ? sizeMatch.quantity : item.quantity
                }
              })
            } else {
              itemsToSend = sizeItems
            }
          }

          return {
            fashionProductId: product.fashionProduct?.id || product.fashionProductId,
            groupId: product.fashionProduct?.group?.id || product.group?.id,
            discount: product.discount,
            colorCount: product.colorCount,
            totalItems: product.totalItems,
            subtotal: product.subtotal,
            items: itemsToSend,
            colors: product.colors,
            selectedColors: product.selectedColors,
          }
        }),
      }

      if (selectedFiles.length > 0) {
        try {
          setFileUploadStatus("Convertendo arquivos...")
          const attachmentsData = []

          for (const file of selectedFiles) {
            const base64Data = await fileToBase64(file)
            attachmentsData.push({
              file: base64Data,
              fileName: file.name
            })
          }

          payload.attachments = attachmentsData
          console.log(`📦 [EDIT-ORDER] Enviando ${attachmentsData.length} anexo(s):`, attachmentsData.map(a => ({ fileName: a.fileName, size: a.file.length })))
          setFileUploadStatus("Arquivos processados, atualizando pedido...")
        } catch (error) {
          console.error('Erro ao converter arquivos para base64:', error)
          setFileUploadStatus("Erro ao processar arquivos")
          alert('Erro ao processar os arquivos anexos')
          return
        }
      } else {
        console.log('⚠️ [EDIT-ORDER] Nenhum novo anexo selecionado')
      }

      console.log(`🚀 [EDIT-ORDER] Enviando payload para atualização do pedido ${orderId}:`, {
        hasAttachments: !!payload.attachments,
        attachmentsCount: payload.attachments?.length || 0,
        productsCount: payload.products.length
      })

      const response = await axios.put(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      
      setFeedback({
        isOpen: true,
        message: 'Pedido atualizado com sucesso!',
        statusOk: true
      })

      setSelectedFiles([])
      setTimeout(() => setFileUploadStatus(""), 3000)

      const updatedOrder = await fetchOrder()
      if (updatedOrder && updatedOrder.fileInFashionOrder && Array.isArray(updatedOrder.fileInFashionOrder)) {
        const mappedAttachments = updatedOrder.fileInFashionOrder.map((file: any) => ({
          id: file.generalFile.id,
          originalName: file.generalFile.originalName || file.generalFile.title,
          fileName: file.generalFile.fileName,
          filePath: file.generalFile.fileUrl,
          fileSize: file.generalFile.fileSize,
          mimeType: file.generalFile.mimeType,
        }));
        setExistingAttachments(mappedAttachments)
      }

    } catch (error: any) {
      console.error('Erro ao atualizar o pedido:', error)
      setFileUploadStatus("Erro ao atualizar pedido")
      
      setFeedback({
        isOpen: true,
        message: 'Erro ao atualizar o pedido',
        statusOk: false
      })
    } finally {
      if (setParentSaving) setParentSaving(false)
    }
  }, [formData, addedProducts, orderId, cleanCurrency, selectedFiles, setFeedback, setParentSaving])

  useEffect(() => {
    if (handleEditSave.current) {
      console.log('⚙️ [EDIT-ORDER] Configurando handleEditSave.current com handleEditSaveOrder')
      handleEditSave.current = handleEditSaveOrder
    }
  }, [handleEditSaveOrder, handleEditSave])

  const handleRemoveProduct = (indexToRemove: number) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== indexToRemove))
    setOpenProductIndex(null)
  }

  const handleUpdateProduct = (oldProduct: any, updatedProduct: any) => {
    if (openProductIndex !== null) {
      const normalizedProduct = normalizeProductData(updatedProduct)

      setAddedProducts((prevProducts) => {
        const newProducts = [...prevProducts]
        const existingProduct = newProducts[openProductIndex]

        newProducts[openProductIndex] = {
          ...existingProduct,
          ...normalizedProduct,
        }

        return newProducts
      })

      setOpenProductIndex(null)
    } else {
      console.warn("EditFashionOrder - openProductIndex é null, não foi possível atualizar produto.")
    }
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    const normalizedProduct = normalizeProductData(newProduct)

    setAddedProducts((prevProducts) => [...prevProducts, normalizedProduct])
    setShowAddProduct(false)
    setOpenProductIndex(null)
  }

  useEffect(() => {
    let totalItemsCount = 0
    let totalPiecesSum = 0
    let totalCostSum = 0
    let totalDiscountValue = 0

    addedProducts.forEach((product) => {
      totalItemsCount += 1

      const productTotalItems = product.sizeQuantities
        ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
        : product.totalItems || 0

      totalPiecesSum += productTotalItems

      const subtotalWithoutDiscount = product.subtotal || 0

      let discountValue = 0
      if (product.discount && product.discount > 0) {
        const originalSubtotal = subtotalWithoutDiscount / (1 - product.discount / 100)
        discountValue = calculateDiscountValue(originalSubtotal, product.discount)
      }

      totalDiscountValue += discountValue
      totalCostSum += subtotalWithoutDiscount
    })

    setFormData((prevState) => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: formatCurrency(totalCostSum),
    }))
  }, [addedProducts])

  const handleSupplierChange = (supplier: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, supplier }))
  }

  const handleCompanyChange = (company: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, company }))
  }

  const handleSellerChange = (seller: UserItemType) => {
    setFormData((prevState) => ({ ...prevState, seller }))
  }

  const getSafeValue = (value: any): string => {
    if (typeof value === 'object' && value !== null) {
      if ('id' in value && 'name' in value) {
        return value.name || '-'
      }
      return '-'
    }
    return String(value) || '-'
  }

  const calculateDiscountMonetaryValue = (product: ProductApiData): string => {
    if (!product.discount || product.discount <= 0 || !product.subtotal) {
      return "-"
    }

    const originalSubtotal = product.subtotal / (1 - product.discount / 100)

    const discountValue = calculateDiscountValue(originalSubtotal, product.discount)

    return formatCurrency(discountValue)
  }

  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Editar pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid style={{ marginTop: '0' }}>
          <FormGroup>
            <CompanySelect
              label='Fornecedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 2 }}
              value={formData.supplier}
              onChange={handleSupplierChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
              displayField='name'
              value={formData.seller}
              onChange={handleSellerChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 1 }}
              value={formData.company}
              onChange={handleCompanyChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>{isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formData.orderType}
                  onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                >
                  <option value=''>Selecione o tipo de pedido</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type='date'
                  value={formData.startDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type='date'
                  value={formData.finalDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      finalDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <InfiniteSelect
                  label="Condição de pagamento"
                  placeholder="Selecione a condição de pagamento"
                  value={formData.paymentCondition ? { id: formData.paymentCondition, name: formData.paymentCondition } : null}
                  onSelect={(option) => {
                    setFormData({ ...formData, paymentCondition: option?.name || '' })
                  }}
                  mode="crud"
                  allowCreate={true}
                  allowDelete={true}
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                    listPath: '/fashion-order/payment-conditions',
                    createPath: '/fashion-order/payment-conditions',
                    deletePath: '/fashion-order/payment-conditions',
                    searchParam: 'search',
                    limitParam: 'limit',
                    offsetParam: 'offset',
                    transformResponse: (data) => {
                      if (Array.isArray(data)) {
                        return data.map((item: any) => ({ id: item.id, name: item.name }));
                      }
                      return [];
                    },
                    transformCreateResponse: (data) => ({ id: data.id, name: data.name })
                  }}
                />
              </FormGroup>

              <FormGroup>
                <InfiniteSelect
                  label="Tipo de frete"
                  placeholder="Selecione o tipo de frete"
                  value={formData.shippingType ? { id: formData.shippingType, name: formData.shippingType } : null}
                  onSelect={(option) => {
                    setFormData({ ...formData, shippingType: option?.name || '' })
                  }}
                  mode="crud"
                  allowCreate={true}
                  allowDelete={true}
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                    listPath: '/fashion-order/shipping-types',
                    createPath: '/fashion-order/shipping-types',
                    deletePath: '/fashion-order/shipping-types',
                    searchParam: 'search',
                    limitParam: 'limit',
                    offsetParam: 'offset',
                    transformResponse: (data) => {
                      if (Array.isArray(data)) {
                        return data.map((item: any) => ({ id: item.id, name: item.name }));
                      }
                      return [];
                    },
                    transformCreateResponse: (data) => ({ id: data.id, name: data.name })
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.shippingCost)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({ ...formData, shippingCost: cleanedValue })
                  }}
                  placeholder="Digite o valor do frete"
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText
                  type="text"
                  value={formData.trackingCode}
                  onChange={(e) => setFormData({ ...formData, trackingCode: e.target.value })}
                  placeholder="Digite o código de rastreio"
                />
              </FormGroup>

              <FormGroup>
                <Label>Anexos</Label>
                <AttachmentManager
                  selectedFiles={selectedFiles}
                  existingAttachments={existingAttachments}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  onViewAttachment={handleViewAttachment}
                  onDownloadAttachment={handleDownloadAttachment}
                  onRemoveExistingAttachment={handleRemoveExistingAttachment}
                  formatFileSize={formatFileSize}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.maximumBillingAmount)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({
                      ...formData,
                      maximumBillingAmount: cleanedValue,
                    })
                  }}
                  className="w-full rounded bg-gray-100 p-2"
                  placeholder="Digite o valor máximo"
                />
              </FormGroup>
            </FormGrid>
            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type='text'
                  value={formData.extraInformation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extraInformation: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>

          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formData.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formData.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formData.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      <>
        {Array.isArray(addedProducts) && addedProducts.length > 0 && (
          <>
            {addedProducts.map((product, index) => {
              const productName = product.fashionProduct?.name || product.name || "-"
              const familyName = product.fashionProduct?.group?.family?.name || product.family?.name || "-"
              const groupName = product.fashionProduct?.group?.name || product.group?.name || "-"

              const priceValue =
                typeof product.fashionProduct?.price === "number"
                  ? `R$ ${product.fashionProduct.price.toFixed(2).replace(".", ",")}`
                  : "-"

              const subtotalValue =
                typeof product.subtotal === "number" ? formatCurrency(product.subtotal) : "-"

              const currentTotalItems = product.sizeQuantities
                ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
                : product.totalItems || 0

              const productFields = [
                { label: 'Produto', value: productName, showIcon: true },
                { label: 'Família', value: familyName, showIcon: true },
                {
                  label: "Cores",
                  value: product.colorCount ?? "-",
                  showIcon: true,
                },
                { label: "Precificação", value: priceValue, showIcon: true },
                {
                  label: "Quantidade de peças",
                  value: currentTotalItems ?? "-",
                  showIcon: true,
                },
                {
                  label: "Desconto",
                  value: calculateDiscountMonetaryValue(product),
                  showIcon: true,
                },
                { label: "Subtotal", value: subtotalValue, showIcon: false },
              ]

              return (
                <Card key={product.fashionProductId || index}>
                  <ProductShort>
                    <ProductDiv>
                      {productFields.map(({ label, value, showIcon }, i) => (
                        <ShortLabel key={i}>
                          <ProductFieldLabel>{label}:</ProductFieldLabel> {getSafeValue(value)}{" "}
                          {showIcon && (
                            <img style={{ marginLeft: "0.5rem" }} src={rectangle || "/placeholder.svg"} alt="" />
                          )}
                        </ShortLabel>
                      ))}
                      <ProductFieldsContainer>
                        <img
                          src={trash}
                          alt='Remover'
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveProduct(index)}
                        />
                        <Button
                          onClick={() => {
                            setOpenProductIndex(openProductIndex === index ? null : index)
                          }}
                        >
                          {openProductIndex === index ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </Button>
                      </ProductFieldsContainer>
                    </ProductDiv>
                  </ProductShort>

                  {openProductIndex === index && (
                    <AddProductForm
                      order={addedProducts}
                      setOrder={setAddedProducts}
                      product={product}
                      onClose={() => {
                        setOpenProductIndex(null)
                      }}
                      onProductUpdated={handleUpdateProduct}
                      onRemoveProduct={handleRemoveProduct}
                      isEditing={true}
                      onProductAdded={handleNewProductAdded}
                    />
                  )}
                </Card>
              )
            })}
          </>
        )}
      </>

      <AddProductContainer>
        {showAddProduct && (
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={undefined}
            onClose={() => setShowAddProduct(false)}
            onRemoveProduct={handleRemoveProduct}
            onProductUpdated={handleUpdateProduct}
            isEditing={false}
            onProductAdded={handleNewProductAdded}
          />
        )}
        <AddButton
          onClick={() => {
            setShowAddProduct(!showAddProduct)
            setOpenProductIndex(null)
          }}
        >
          <AddButtonImg src={plus} alt='Adicionar' />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  )
}

export default EditFashionOrder